import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Send, BadgeCheck } from "lucide-react";

/**
 * WaitlistPanel — admin-side counter + notify trigger for the visitor waitlist.
 *
 * Surfaces which waitlist subscribers would be matched if THIS piece publishes now,
 * and lets the admin "notify" them — currently MOCKED while Resend is offline, but
 * each notify already records `notified_for` on the subscriber so the same person
 * isn't re-alerted on the next publish.
 */
export default function WaitlistPanel({ itemId, stage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/inventory/${itemId}/waitlist_matches`);
      setData(res);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("waitlist matches load failed", e);
      }
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  const notify = async () => {
    setNotifying(true);
    try {
      const { data: res } = await api.post(`/inventory/${itemId}/waitlist_notify`);
      toast.success(res.mock_send ? `queued ${res.notified} notification${res.notified === 1 ? "" : "s"} (mock send — resend not yet live)` : `notified ${res.notified} subscriber${res.notified === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error("notify failed.");
    } finally {
      setNotifying(false);
    }
  };

  if (loading && !data) return null;
  if (!data) return null;

  const matches = data.matches || [];
  const close = matches.filter((m) => m.is_close_fit);
  const total = data.total_in_category || 0;

  if (total === 0) {
    return (
      <div className="border border-dashed border-taupe/60 p-4 text-xs text-taupe italic" data-testid="waitlist-panel-empty">
        no one is waitlisted in this category yet.
      </div>
    );
  }

  return (
    <div className="border border-taupe/50 bg-ivory p-5" data-testid="waitlist-panel">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="editorial-label flex items-center gap-2"><Mail className="w-3 h-3" /> first-look list</p>
          <p className="text-sm text-charcoal mt-2">
            <span className="font-serif text-2xl text-brass tabular-nums">{close.length}</span>
            <span className="text-xs text-espresso ml-2">close-fit match{close.length === 1 ? "" : "es"} · within {data.fit_threshold_cm} cm</span>
          </p>
          <p className="text-[0.65rem] tracking-widest uppercase text-taupe mt-1">{total} subscriber{total === 1 ? "" : "s"} in this category</p>
        </div>
        <button
          onClick={notify}
          disabled={notifying || close.length === 0}
          data-testid="waitlist-notify-btn"
          className="inline-flex items-center gap-2 bg-brass hover:bg-espresso text-ivory px-4 py-2 text-[0.65rem] tracking-[0.25em] uppercase disabled:opacity-40"
        >
          {notifying ? "notifying…" : <><Send className="w-3 h-3" /> notify matches</>}
        </button>
      </div>

      {matches.length > 0 && (
        <ul className="mt-3 divide-y divide-taupe/30 max-h-56 overflow-auto" data-testid="waitlist-rows">
          {matches.map((m) => (
            <li key={m.id} className="py-2 flex items-center justify-between text-xs" data-testid={`waitlist-row-${m.id}`}>
              <span className="text-charcoal truncate">{m.email}</span>
              <span className={`tabular-nums ${m.is_close_fit ? "text-brass" : "text-taupe"} flex items-center gap-1`}>
                {m.is_close_fit && <BadgeCheck className="w-3 h-3" />}
                {m.score_cm === null ? "no profile" : `${m.score_cm} cm Δ`}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="text-[0.6rem] tracking-widest uppercase text-taupe mt-3">
        stage · {stage || "—"} · notifications mocked until resend goes live
      </p>
    </div>
  );
}
